"use client";
import React, { useState, useRef, useEffect } from "react";

type TooltipPosition = "bottom-left" | "bottom-right" | "top-left" | "top-right";

type TooltipProps = {
  children: React.ReactNode;
  className?: string;
  content: React.ReactNode;
  position?: TooltipPosition;
  trigger?: "hover" | "click";
  mode?: "dark" | "light";
  maxSize?: string;
};

export default function Tooltip({
  children,
  className,
  content,
  position = "bottom-left",
  trigger = "hover",
  mode = "light",
  maxSize = "max-w-80",
}: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (trigger !== "click") return;

    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setVisible(false);
      }
    };

    const handleScroll = () => setVisible(false);

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [trigger]);

  // const positionClasses = {
  //   left: "top-full left-0 mt-2",
  //   right: "top-full right-0 mt-2",
  // };

  // const trianglePosition = {
  //   left: "left-4",
  //   right: "right-4",
  // };

  const positionClasses: Record<TooltipPosition, string> = {
    "bottom-left": "top-full left-0 mt-2",
    "bottom-right": "top-full right-0 mt-2",
    "top-left": "bottom-full left-0 mb-2",
    "top-right": "bottom-full right-0 mb-2",
  };

  const triangleClasses: Record<TooltipPosition, string> = {
    "bottom-left": `absolute -top-2 left-4 border-l-8 border-r-8 border-b-8 border-transparent ${mode === "dark" ? "border-b-[#2b2b2b]" : "border-b-white"}`,
    "bottom-right": `absolute -top-2 right-4 border-l-8 border-r-8 border-b-8 border-transparent ${mode === "dark" ? "border-b-[#2b2b2b]" : "border-b-white"}`,

    "top-left": `absolute -bottom-2 left-4 border-l-8 border-r-8 border-t-8 border-transparent ${mode === "dark" ? "border-t-[#2b2b2b]" : "border-t-white"}`,
    "top-right": `absolute -bottom-2 right-4 border-l-8 border-r-8 border-t-8 border-transparent ${mode === "dark" ? "border-t-[#2b2b2b]" : "border-t-white"}`,
  };

  return (
    <div
      className={`relative inline-block ${className}`}
      ref={tooltipRef}
      {...(trigger === "hover"
        ? {
            onMouseEnter: () => setVisible(true),
            onMouseLeave: () => setVisible(false),
          }
        : {
            onClick: (e) => {
              e.preventDefault();
              setVisible((v) => !v);
            },
          })}
    >
      {children}

      {visible && (
        <div
          className={`absolute z-10 ${positionClasses[position]} ${mode === "dark" ? "bg-[#2b2b2b] text-[#f9f9f9]" : "bg-white text-[#444444]"} border border-[#0000000F] w-max ${maxSize} text-xs px-3 py-2 rounded-2lg shadow-[0px_0px_15px_2px_#00000025]`}
        >
          <div className={`w-0 h-0 ${triangleClasses[position]}`} />
          {content}
        </div>
      )}
    </div>
  );
}

/* use Case of this tooltip component
 
<Tooltip content="Link Copied" position="right">
  <button></button>
</Tooltip> */
